import React, { useState, useEffect } from "react";
import {
  CreditCardIcon,
  BuildingOffice2Icon,
  PaperAirplaneIcon,
  TicketIcon,
  PhotoIcon,
  UsersIcon,
  UserIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { formatPrice, formatImageUrl, getFlagImage } from "@/lib/general";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useBudgetStore from "@/stores/tripBuilder/useBudgetStore";
import useCurrencyStore from "@/stores/ui/currencyStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import logger from "@/utils/logger";

const COLORS = {
  accommodation: "#FF6B6B", // Vibrant coral red
  transport: "#4ECDC4", // Bright turquoise
  activities: "#FFD93D", // Sunny yellow
};

interface BudgetItemProps {
  name: string;
  cost: number;
  currencyCode: string;
  image?: string;
  duration?: number;
}

const BudgetItem: React.FC<BudgetItemProps> = ({
  name,
  cost,
  currencyCode,
  image,
  duration,
}) => (
  <div className="flex justify-between items-center py-2.5 hover:bg-neutral-50 rounded-lg px-2 transition-colors">
    <div className="flex items-center space-x-3">
      <div className="w-6 h-6 rounded-md overflow-hidden bg-neutral-100 flex items-center justify-center flex-shrink-0">
        {image ? (
          <img
            src={formatImageUrl(image, "medium")}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <PhotoIcon className="w-4 h-4 text-neutral-400" />
        )}
      </div>
      <span className="text-xs text-neutral-700 font-light max-w-[calc(100%-20px)] truncate">
        {name}
      </span>
    </div>
    <span className="text-xs font-light text-neutral-900">
      {formatPrice(currencyCode, cost)} {duration ? `x ${duration} nights` : ""}
    </span>
  </div>
);

const BudgetCategory: React.FC<{
  name: string;
  icon: React.ReactNode;
  value: number;
  color: string;
  percentage: number;
  currencyCode: string;
  items: {
    name: string;
    cost: number;
    image?: string;
    duration?: number;
    participants?: number;
  }[];
  onEdit?: (value: number) => void;
}> = ({ name, icon, value, color, percentage, currencyCode, items }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="space-y-3 bg-white rounded-xl p-4 shadow-sm border border-neutral-100">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div
            className="p-2.5 rounded-lg"
            style={{ backgroundColor: `${color}15` }}
          >
            {icon}
          </div>
          <div className="flex flex-col items-start">
            <p className="text-sm font-medium text-neutral-900">{name}</p>
            <p className="text-xs text-neutral-500 font-light mt-0.5">
              {percentage}% of total
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <p className="text-sm font-medium text-neutral-900">
            {formatPrice(currencyCode, value)}
          </p>
          {/* {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(value);
              }}
              className="h-8 px-3 hover:bg-neutral-100"
            >
              Edit
            </Button>
          )} */}
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-1">
          {items.length > 0 ? (
            items.map((item, index) => (
              <BudgetItem
                key={index}
                name={item.name}
                cost={item.cost}
                currencyCode={currencyCode}
                image={item.image}
                duration={item.duration}
              />
            ))
          ) : (
            <p className="text-sm text-neutral-500 italic py-2 font-light">
              No items added yet
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface BudgetViewProps {
  locationId: string;
}

const BudgetView: React.FC<BudgetViewProps> = ({ locationId }) => {
  const { selectedCurrency } = useCurrencyStore();
  const { isParticipant, setPermissionDialogOpen, selectedLocation } =
    useTripStore();

  const {
    locationBudget,
    budgetBreakdown,
    participantBudgets,
    setCurrentLocationId,
    calculateBudget,
  } = useBudgetStore();

  logger.log("ParticipantBudgets", participantBudgets);

  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [showPersonalBudget, setShowPersonalBudget] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  useEffect(() => {
    if (locationId) {
      setCurrentLocationId(locationId);
    }
  }, [locationId, setCurrentLocationId]);

  const handleModalOpen = () => {
    if (!isParticipant) {
      setPermissionDialogOpen(true);
      return;
    }
    if (locationId) {
      calculateBudget(locationId);
    }
    setIsBudgetModalOpen(true);
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((key) => key !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const categories = [
    {
      name: "Accommodation",
      key: "accommodation" as const,
      icon: (
        <BuildingOffice2Icon
          className="w-4 h-4"
          style={{ color: COLORS.accommodation }}
        />
      ),
      value: locationBudget.accommodation,
      color: COLORS.accommodation,
      items: budgetBreakdown.accommodation,
    },
    {
      name: "Transport",
      key: "transport" as const,
      icon: (
        <PaperAirplaneIcon
          className="w-4 h-4"
          style={{ color: COLORS.transport }}
        />
      ),
      value: locationBudget.transport,
      color: COLORS.transport,
      items: budgetBreakdown.transport,
    },
    {
      name: "Activities",
      key: "activities" as const,
      icon: (
        <TicketIcon className="w-4 h-4" style={{ color: COLORS.activities }} />
      ),
      value: locationBudget.activities,
      color: COLORS.activities,
      items: budgetBreakdown.activities,
    },
  ];

  return (
    <>
      <button
        onClick={handleModalOpen}
        className="w-1/2 h-full bg-white rounded-lg p-2 2xl:p-2.5 flex flex-col justify-between items-start group hover:ring-1 hover:ring-indigo-500/50 hover:bg-indigo-50 transition-all duration-300 shadow-sm border border-neutral-100"
        data-tutorial="overview-budget"
      >
        <div className="flex flex-row items-center space-x-1.5">
          <CreditCardIcon className="w-4 h-4 text-neutral-800" />
          <h2 className="text-xs 2xl:text-[15px] text-neutral-400 font-light group-hover:text-indigo-600 transition-all duration-300">
            Budget
          </h2>
        </div>
        <h3 className="text-neutral-800 font-medium ~lg/3xl ~text-sm/xl group-hover:text-indigo-600 transition-all duration-300 pt-1">
          {formatPrice(selectedCurrency.code, locationBudget.total)}
        </h3>
      </button>
      <Dialog open={isBudgetModalOpen} onOpenChange={setIsBudgetModalOpen}>
        <DialogContent className="sm:max-w-[600px] 2xl:max-w-[700px] p-6">
          <DialogHeader className="pb-4 border-b">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-6">
                <DialogTitle className="text-xl 2xl:text-2xl font-semibold text-neutral-900">
                  {selectedLocation?.name} Budget
                </DialogTitle>
                <div className="flex items-center gap-2 bg-neutral-100 p-1 2xl:p-1.5 rounded-lg">
                  <button
                    onClick={() => setShowPersonalBudget(false)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] 2xl:text-xs font-medium transition-all duration-200",
                      !showPersonalBudget
                        ? "bg-white text-neutral-900 shadow-sm"
                        : "text-neutral-500 hover:text-neutral-700"
                    )}
                  >
                    <span>Overall</span>
                  </button>
                  <button
                    onClick={() => setShowPersonalBudget(true)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] 2xl:text-xs font-medium transition-all duration-200",
                      showPersonalBudget
                        ? "bg-white text-neutral-900 shadow-sm"
                        : "text-neutral-500 hover:text-neutral-700"
                    )}
                  >
                    <span>Personal</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-gradient-to-r from-indigo-50 to-indigo-100/50 py-1.5 px-2 2xl:py-2 2xl:px-3 rounded-lg shadow-sm">
                    <img
                      src={getFlagImage(selectedCurrency.country)}
                      alt={selectedCurrency.code}
                      className="w-4 h-4 2xl:w-5 2xl:h-5"
                    />
                    <span className="text-sm 2xl:text-base font-semibold text-indigo-700">
                      {formatPrice(selectedCurrency.code, locationBudget.total)}
                    </span>
                  </div>
                  <DialogDescription className="text-xs 2xl:text-sm text-neutral-500">
                    Total budget for your stay
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-1.5 text-xs 2xl:text-sm text-neutral-500">
                  <UsersIcon className="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />
                  <span>
                    {selectedLocation?.participants.length || 0}{" "}
                    {selectedLocation?.participants.length === 1
                      ? "participant"
                      : "participants"}
                  </span>
                </div>
              </div>
            </div>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {showPersonalBudget ? (
              <motion.div
                key="personal"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <ScrollArea className="h-[300px] 2xl:h-[400px]">
                  {selectedLocation && participantBudgets.length > 0 ? (
                    <div className="space-y-3 py-3">
                      <div className="grid grid-cols-1 gap-4">
                        {participantBudgets.map((item, index: number) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg p-4 2xl:p-5 border border-neutral-100 shadow-sm hover:border-indigo-100 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-full overflow-hidden bg-neutral-100 flex items-center justify-center flex-shrink-0">
                                  {item.participant.waytora_avatar_url ? (
                                    <AvatarImage
                                      src={item.participant.waytora_avatar_url}
                                      alt={item.participant.name}
                                      className="w-full h-full object-cover"
                                      firstName={
                                        item.participant.name
                                          ? item.participant.name.split(" ")[0]
                                          : ""
                                      }
                                      lastName={
                                        item.participant.name
                                          ? item.participant.name.split(" ")[1]
                                          : ""
                                      }
                                    />
                                  ) : (
                                    <UserIcon className="w-5 h-5 2xl:w-6 2xl:h-6 text-neutral-400" />
                                  )}
                                </div>
                                <div>
                                  <h3 className="font-medium text-neutral-900 2xl:text-lg">
                                    {item.participant.name}
                                  </h3>
                                  <p className="text-xs 2xl:text-sm font-light text-neutral-500">
                                    {formatPrice(
                                      selectedCurrency.code,
                                      item.total
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between gap-8 text-xs 2xl:text-sm">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-2 h-2 2xl:w-2.5 2xl:h-2.5 rounded-full"
                                      style={{
                                        backgroundColor: COLORS.accommodation,
                                      }}
                                    ></div>
                                    <span className="font-light text-neutral-500">
                                      Accommodation
                                    </span>
                                  </div>
                                  <span className="font-medium text-neutral-700">
                                    {formatPrice(
                                      selectedCurrency.code,
                                      item.accommodationShare
                                    )}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-8 text-xs 2xl:text-sm">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-2 h-2 2xl:w-2.5 2xl:h-2.5 rounded-full"
                                      style={{
                                        backgroundColor: COLORS.transport,
                                      }}
                                    ></div>
                                    <span className="font-light text-neutral-500">
                                      Transport
                                    </span>
                                  </div>
                                  <span className="font-medium text-neutral-700">
                                    {formatPrice(
                                      selectedCurrency.code,
                                      item.transportShare
                                    )}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-8 text-xs 2xl:text-sm">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-2 h-2 2xl:w-2.5 2xl:h-2.5 rounded-full"
                                      style={{
                                        backgroundColor: COLORS.activities,
                                      }}
                                    ></div>
                                    <span className="font-light text-neutral-500">
                                      Activities
                                    </span>
                                  </div>
                                  <span className="font-medium text-neutral-700">
                                    {formatPrice(
                                      selectedCurrency.code,
                                      item.activitiesShare
                                    )}
                                  </span>
                                </div>
                                <div className="h-px bg-neutral-100 my-2" />
                                <div className="flex items-center justify-between gap-8 text-xs 2xl:text-sm">
                                  <span className="font-medium text-neutral-900">
                                    Total
                                  </span>
                                  <span className="font-semibold text-indigo-600">
                                    {formatPrice(
                                      selectedCurrency.code,
                                      item.total
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t">
                              <div className="flex gap-1 w-full h-2 2xl:h-2.5">
                                {item.total > 0 && (
                                  <>
                                    <div
                                      className="h-2 2xl:h-2.5 rounded-full"
                                      style={{
                                        width: `${
                                          (item.accommodationShare /
                                            item.total) *
                                          100
                                        }%`,
                                        backgroundColor: COLORS.accommodation,
                                      }}
                                    />
                                    <div
                                      className="h-2 2xl:h-2.5 rounded-full"
                                      style={{
                                        width: `${
                                          (item.transportShare / item.total) *
                                          100
                                        }%`,
                                        backgroundColor: COLORS.transport,
                                      }}
                                    />
                                    <div
                                      className="h-2 2xl:h-2.5 rounded-full"
                                      style={{
                                        width: `${
                                          (item.activitiesShare / item.total) *
                                          100
                                        }%`,
                                        backgroundColor: COLORS.activities,
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                              <div className="flex justify-between mt-2 text-xs 2xl:text-sm text-neutral-500">
                                <span>
                                  Accommodation{" "}
                                  {item.total === 0
                                    ? 0
                                    : Math.round(
                                        (item.accommodationShare / item.total) *
                                          100
                                      )}
                                  %
                                </span>
                                <span>
                                  Transport{" "}
                                  {item.total === 0
                                    ? 0
                                    : Math.round(
                                        (item.transportShare / item.total) * 100
                                      )}
                                  %
                                </span>
                                <span>
                                  Activities{" "}
                                  {item.total === 0
                                    ? 0
                                    : Math.round(
                                        (item.activitiesShare / item.total) *
                                          100
                                      )}
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="h-[300px] flex flex-col items-center justify-center text-center">
                      <UsersIcon className="w-12 h-12 2xl:w-14 2xl:h-14 text-neutral-300 mb-3" />
                      <p className="text-sm 2xl:text-base text-neutral-500 max-w-[250px] 2xl:max-w-[300px]">
                        No participant budget data available for this location
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </motion.div>
            ) : (
              <motion.div
                key="overall"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <ScrollArea className="h-[300px] 2xl:h-[400px] pr-3">
                  <div className="space-y-3 py-3">
                    <div className="grid grid-cols-1 gap-4">
                      {categories.map((category) => (
                        <div
                          key={category.key}
                          className="bg-white rounded-lg p-2.5 2xl:p-3 border border-neutral-100 shadow-sm hover:border-indigo-100 transition-colors"
                        >
                          <button
                            onClick={() => toggleCategory(category.key)}
                            className="w-full flex items-start justify-between"
                          >
                            <div className="flex items-center space-x-3">
                              <div
                                className="p-2 2xl:p-2.5 rounded-lg"
                                style={{
                                  backgroundColor: `${category.color}15`,
                                }}
                              >
                                {category.icon}
                              </div>
                              <div className="flex flex-col items-start space-y-1">
                                <h3 className="text-sm 2xl:text-base font-medium text-neutral-900">
                                  {category.name}
                                </h3>
                                <p className="text-xs 2xl:text-sm font-light text-neutral-500">
                                  {locationBudget.total > 0
                                    ? `${Math.round(
                                        (category.value /
                                          locationBudget.total) *
                                          100
                                      )}% of total`
                                    : "No expenses added yet"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs 2xl:text-base font-normal text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                                {formatPrice(
                                  selectedCurrency.code,
                                  category.value
                                )}
                              </span>
                              <motion.div
                                animate={{
                                  rotate: expandedCategories.includes(
                                    category.key
                                  )
                                    ? 180
                                    : 0,
                                }}
                                transition={{ duration: 0.2 }}
                              >
                                <ChevronDownIcon className="w-4 h-4 2xl:w-5 2xl:h-5 text-neutral-500" />
                              </motion.div>
                            </div>
                          </button>
                          <motion.div
                            initial={false}
                            animate={{
                              height: expandedCategories.includes(category.key)
                                ? "auto"
                                : 0,
                              opacity: expandedCategories.includes(category.key)
                                ? 1
                                : 0,
                            }}
                            transition={{
                              duration: 0.2,
                              ease: "easeInOut",
                            }}
                            className="overflow-hidden"
                          >
                            <div
                              className={cn(
                                "pt-2 2xl:pt-3",
                                expandedCategories.includes(category.key)
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            >
                              {category.items.length > 0 ? (
                                <div className="space-y-1.5 2xl:space-y-2">
                                  {category.items.map((item, index) => (
                                    <motion.div
                                      key={index}
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{
                                        duration: 0.2,
                                        delay: index * 0.05,
                                      }}
                                      className="flex items-center justify-between py-2 px-3 bg-neutral-50 rounded-lg"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <div className="w-6 h-6 2xl:w-7 2xl:h-7 rounded-md overflow-hidden bg-white flex items-center justify-center">
                                          {item.image ? (
                                            <img
                                              src={formatImageUrl(
                                                item.image,
                                                "medium"
                                              )}
                                              alt={item.name}
                                              className="w-full h-full object-cover"
                                            />
                                          ) : (
                                            <PhotoIcon className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-neutral-400" />
                                          )}
                                        </div>
                                        <span className="text-xs 2xl:text-sm font-light text-neutral-700 max-w-[250px] truncate">
                                          {item.name}
                                        </span>
                                      </div>
                                      <span className="text-xs 2xl:text-sm font-normal text-neutral-900">
                                        {formatPrice(
                                          selectedCurrency.code,
                                          item.cost
                                        )}
                                        {item.duration
                                          ? ` x ${item.duration} nights`
                                          : ""}
                                        {category.key === "transport"
                                          ? ` x ${
                                              selectedLocation?.participants
                                                .length
                                            } ${
                                              selectedLocation?.participants
                                                .length === 1
                                                ? "ticket"
                                                : "tickets"
                                            }`
                                          : ""}
                                        {category.key === "activities" &&
                                        item.participants
                                          ? ` x ${item.participants} ${
                                              item.participants === 1
                                                ? "ticket"
                                                : "tickets"
                                            }`
                                          : ""}
                                      </span>
                                    </motion.div>
                                  ))}
                                </div>
                              ) : (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="py-2 px-3 bg-neutral-50 rounded-lg"
                                >
                                  <p className="text-xs 2xl:text-sm font-light text-neutral-500 text-center">
                                    No {category.name.toLowerCase()} added yet
                                  </p>
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        </div>
                      ))}
                    </div>
                  </div>
                </ScrollArea>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BudgetView;
